import React, { useState } from 'react';
import { Mail, Star, Archive, Trash2, Clock, Search, Filter, Plus } from 'lucide-react';
import EmailList from './email/EmailList';
import EmailCompose from './email/EmailCompose';
import EmailTemplates from './email/EmailTemplates';
import EmailFilters from './email/EmailFilters';
import EmailBulkActions from './email/EmailBulkActions';
import EmailView from './email/EmailView';
import { useEmails } from '../../hooks/useEmails';
import { Email } from '../../types/email';
import { useNavigate } from 'react-router-dom';

export default function EmailDashboard() {
  const [selectedFolder, setSelectedFolder] = useState<Email['folder']>('inbox');
  const [showCompose, setShowCompose] = useState(false);
  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showTemplates, setShowTemplates] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<Email | null>(null);

  const {
    emails,
    sendEmail,
    moveToFolder,
    toggleStar,
    markAsRead,
    deleteEmail
  } = useEmails(selectedFolder);

  const handleArchive = (emailId: string) => {
    moveToFolder(emailId, 'archived');
  };

  const handleDelete = (emailId: string) => {
    deleteEmail(emailId);
  };

  const handleBulkAction = (action: 'archive' | 'delete' | 'mark_read') => {
    selectedEmails.forEach(emailId => {
      switch (action) {
        case 'archive':
          handleArchive(emailId);
          break;
        case 'delete':
          handleDelete(emailId);
          break;
        case 'mark_read':
          markAsRead(emailId);
          break;
      }
    });
    setSelectedEmails([]);
  };

  const handleEmailSelect = (email: Email) => {
    setSelectedEmail(email);
    markAsRead(email.id);
  };

  const handleViewDealDetails = (dealId: string) => {
    // Navigate to deal details in pipeline
    window.location.href = `/pipeline?dealId=${dealId}`;
  };

  const filteredEmails = emails.filter(email =>
    email.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
    email.from.toLowerCase().includes(searchTerm.toLowerCase()) ||
    email.content.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="h-full">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Email Management</h2>
        <p className="mt-2 text-gray-600">Manage all your M&A communications</p>
      </div>

      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-3">
          <div className="bg-white rounded-lg shadow-sm p-4">
            <button
              onClick={() => setShowCompose(true)}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 mb-4"
            >
              <div className="flex items-center justify-center">
                <Plus className="w-4 h-4 mr-2" />
                Compose
              </div>
            </button>

            <div className="space-y-2">
              {[
                { id: 'inbox', name: 'Inbox', icon: <Mail className="w-4 h-4" />, count: emails.filter(e => !e.isRead).length },
                { id: 'starred', name: 'Starred', icon: <Star className="w-4 h-4" />, count: emails.filter(e => e.isStarred).length },
                { id: 'archived', name: 'Archived', icon: <Archive className="w-4 h-4" />, count: emails.filter(e => e.folder === 'archived').length },
                { id: 'trash', name: 'Trash', icon: <Trash2 className="w-4 h-4" />, count: emails.filter(e => e.folder === 'trash').length }
              ].map((folder) => (
                <button
                  key={folder.id}
                  onClick={() => setSelectedFolder(folder.id as Email['folder'])}
                  className={`w-full flex items-center justify-between p-2 rounded-lg ${
                    selectedFolder === folder.id
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center">
                    {folder.icon}
                    <span className="ml-2">{folder.name}</span>
                  </div>
                  <span className="text-xs bg-gray-100 px-2 py-1 rounded-full">
                    {folder.count}
                  </span>
                </button>
              ))}
            </div>

            <div className="mt-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-900">Templates</h3>
                <button
                  onClick={() => setShowTemplates(!showTemplates)}
                  className="text-sm text-blue-600 hover:text-blue-700"
                >
                  {showTemplates ? 'Hide Templates' : 'Show Templates'}
                </button>
              </div>

              {showTemplates && (
                <EmailTemplates onUseTemplate={() => setShowCompose(true)} />
              )}
            </div>
          </div>
        </div>

        <div className="col-span-9">
          <div className="bg-white rounded-lg shadow-sm">
            <EmailFilters
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
            />

            <EmailList
              emails={filteredEmails}
              onEmailSelect={handleEmailSelect}
              onStarToggle={toggleStar}
              onDelete={handleDelete}
              onArchive={handleArchive}
            />
          </div>
        </div>
      </div>

      {selectedEmails.length > 0 && (
        <EmailBulkActions
          selectedCount={selectedEmails.length}
          onAction={handleBulkAction}
        />
      )}

      {showCompose && (
        <EmailCompose
          onClose={() => setShowCompose(false)}
          onSend={(email) => {
            sendEmail(email);
            setShowCompose(false);
          }}
        />
      )}

      {selectedEmail && (
        <EmailView
          email={selectedEmail}
          onClose={() => setSelectedEmail(null)}
          onReply={(email) => {
            setSelectedEmail(null);
            setShowCompose(true);
          }}
          onForward={(email) => {
            setSelectedEmail(null);
            setShowCompose(true);
          }}
          onDelete={handleDelete}
          onStar={toggleStar}
          onArchive={handleArchive}
          onViewDealDetails={handleViewDealDetails}
        />
      )}
    </div>
  );
}